import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { TopicView } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Modal } from '../ui.js';

const PALETTE = ['#6366f1', '#8b5cf6', '#0ea5e9', '#14b8a6', '#22c55e', '#eab308', '#f97316', '#ec4899'];

/** Light tinted background derived from the topic colour, for legible chips. */
function chipStyle(color: string) {
  return { backgroundColor: `${color}1f`, color };
}

export function TopicChipList({ topicIds, topics }: { topicIds: string[]; topics: TopicView[] }) {
  const resolved = topicIds
    .map((id) => topics.find((topic) => topic.id === id))
    .filter((topic): topic is TopicView => Boolean(topic));

  if (resolved.length === 0) return null;

  return (
    <span className="flex flex-wrap gap-1">
      {resolved.map((topic) => (
        <span key={topic.id} className="chip" style={chipStyle(topic.color)}>
          {topic.name}
        </span>
      ))}
    </span>
  );
}

export function TopicPicker({
  commentId,
  topicIds,
  topics,
  open,
  onClose,
}: {
  commentId: string;
  topicIds: string[];
  topics: TopicView[];
  open: boolean;
  onClose: () => void;
}) {
  const send = useRetroStore((state) => state.send);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return topics;
    return topics.filter((topic) => topic.name.toLowerCase().includes(needle));
  }, [topics, query]);

  const exactMatch = topics.some((topic) => topic.name.toLowerCase() === query.trim().toLowerCase());

  function toggle(topicId: string) {
    const next = topicIds.includes(topicId)
      ? topicIds.filter((id) => id !== topicId)
      : [...topicIds, topicId];
    void send('comment:setTopics', { commentId, topicIds: next });
  }

  async function createAndAttach() {
    const name = query.trim();
    if (!name || creating) return;

    setCreating(true);
    const color = PALETTE[topics.length % PALETTE.length]!;
    const topicId = await send<string>('topic:create', { name, color });
    if (topicId) {
      await send('comment:setTopics', { commentId, topicIds: [...topicIds, topicId] });
      setQuery('');
    }
    setCreating(false);
  }

  return (
    <Modal open={open} onClose={onClose} title="Topics" width="sm">
      <p className="mb-3 text-sm text-ink/60">
        Topics are shared with your team and carry across retros, so recurring themes become visible over
        time.
      </p>

      <input
        className="field mb-3"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search or create a topic"
        maxLength={40}
      />

      <ul className="max-h-64 space-y-1 overflow-y-auto">
        {filtered.map((topic) => {
          const selected = topicIds.includes(topic.id);
          return (
            <li key={topic.id}>
              <button
                type="button"
                onClick={() => toggle(topic.id)}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm transition',
                  selected ? 'bg-black/[0.06]' : 'hover:bg-black/[0.04]',
                )}
              >
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: topic.color }} aria-hidden />
                <span className="flex-1">{topic.name}</span>
                {selected ? <span aria-hidden>✓</span> : null}
              </button>
            </li>
          );
        })}
      </ul>

      {query.trim() && !exactMatch ? (
        <button
          type="button"
          className="btn-primary mt-3 w-full"
          onClick={() => void createAndAttach()}
          disabled={creating}
        >
          Create “{query.trim()}”
        </button>
      ) : null}
    </Modal>
  );
}
